import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../../lib/cms';

const jsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'SoftwareApplication',
      name: 'RevTech + NDS + Xentrios DSO Platform',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description:
        'Integrated RCM automation, dental analytics, data warehousing, denial management, reconciliation, and cloud PMS for U.S. dental groups and DSOs. RevTech DenialIQ, ReconIQ, InsightFlow, ClaimVision, PayerIQ; NDS DataVault; Xentrios PMS.',
      offers: {
        '@type': 'Offer',
        url: 'https://xentrios.com/demo',
      },
      provider: {
        '@type': 'Organization',
        name: 'RevTech Partners',
        url: 'https://revtechpartners.com',
      },
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is an integrated DSO operating platform?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'An integrated DSO operating platform is a unified technology stack that addresses the full range of operational and financial needs of a multi-location dental support organization — without requiring separate point solutions that don\'t share data. For a dental group, this means a single platform covering revenue cycle management (claim submission, denial management, payment reconciliation, AR analytics), data infrastructure (PMS integration, data warehousing, BI connectivity), and practice management (scheduling, clinical records, patient engagement). The RevTech + NDS + Xentrios stack delivers all three layers as an integrated system, with shared data across every product.',
          },
        },
        {
          '@type': 'Question',
          name: 'What products are included in the RevTech + NDS + Xentrios stack?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The full stack includes: DenialIQ (denial management workspace with CARC/RARC categorization and appeal generation), ReconIQ (ERA/EOB payment reconciliation), InsightFlow (enterprise AR analytics and payer performance dashboards), ClaimVision (pre-submission claim intelligence and scrubbing), PayerIQ (payer contract analytics and underpayment detection), NDS DataVault (PMS integration and dental data warehouse for Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS), Exocomps RMM (IT fleet management and remote monitoring for dental offices), and Xentrios (cloud-native dental practice management system).',
          },
        },
        {
          '@type': 'Question',
          name: 'How does this platform compare to using separate point solutions?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Point solutions each address one problem in isolation — a denial management vendor, a separate analytics tool, a data warehouse built by someone else, and a PMS that wasn\'t designed to work with any of them. The result is fragmented data, manual reconciliation between systems, duplicate vendor relationships to manage, and a technology estate that grows more complex with each acquisition. The RevTech + NDS + Xentrios stack is designed as an integrated system: the same claim record flows from ClaimVision pre-submission scrubbing through DenialIQ denial management to ReconIQ reconciliation and InsightFlow analytics — with no re-entry, no re-mapping, no integration projects between vendors.',
          },
        },
        {
          '@type': 'Question',
          name: 'What size DSO is this platform designed for?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The platform is designed for U.S. dental groups operating 5 to 500+ locations. For smaller groups (5–25 locations), the value is in eliminating manual RCM processes and getting enterprise-level analytics without the overhead of building a data infrastructure. For mid-size groups (25–100 locations), the value is in cross-location benchmarking, multi-PMS data integration, and systematic denial management at scale. For large groups (100+ locations), the value is in the data warehouse and BI connectivity — providing the analytics foundation needed to manage a complex, multi-state, multi-PMS organization with real-time data.',
          },
        },
      ],
    },
  ],
};

const painPoints = [
  {
    category: 'Revenue Cycle',
    color: '#c62828',
    icon: '💰',
    problems: [
      'Denial rate is above industry benchmark but root causes aren\'t visible',
      'AR specialists work from static aging reports without workflow tooling',
      'ERA/EOB reconciliation is manual and error-prone',
      'Appeal deadlines are missed because tracking is in spreadsheets',
      'No visibility into payer contract compliance or underpayments',
    ],
    solution: 'RevTech DenialIQ, ReconIQ, InsightFlow, ClaimVision, and PayerIQ — purpose-built dental RCM tools that share a single claim data model from submission through reconciliation.',
  },
  {
    category: 'Data & Reporting',
    color: '#1565c0',
    icon: '📊',
    problems: [
      'Different PMS systems at different locations produce incomparable reports',
      'Enterprise analytics require manual exports and spreadsheet consolidation',
      'Finance team can\'t get consolidated AR without waiting for IT',
      'Power BI or Tableau projects stall because PMS data is too messy',
      'Historical data is trapped in on-premise PMS systems',
    ],
    solution: 'NDS DataVault provides a dental data warehouse that integrates Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS — clean, normalized, and ready for InsightFlow or any BI tool.',
  },
  {
    category: 'Practice Operations',
    color: '#2e7d32',
    icon: '🏥',
    problems: [
      'Aging PMS at acquired locations creates training and support overhead',
      'Clinical documentation workflows vary by location and aren\'t standardized',
      'IT fleet management across 50+ locations is reactive and inefficient',
      'Moving to a modern PMS requires a disruptive migration project',
      'New location onboarding takes months because of technology setup',
    ],
    solution: 'Xentrios cloud-native PMS provides a modern, web-based alternative for new or migrating locations. Exocomps RMM handles IT fleet management across all locations.',
  },
];

const products = [
  {
    name: 'DenialIQ',
    category: 'Denial Management',
    company: 'RevTech',
    desc: 'Denial management workspace: CARC/RARC categorization, AI-assisted appeal letter generation, payer trend dashboards, root cause analytics, and ReconIQ integration.',
    color: '#c62828',
  },
  {
    name: 'ReconIQ',
    category: 'Payment Reconciliation',
    company: 'RevTech',
    desc: 'ERA/EOB payment reconciliation platform: match payments to claims, identify short-pays and contractual adjustments, close the loop between denials and the general ledger.',
    color: '#e65100',
  },
  {
    name: 'InsightFlow',
    category: 'AR Analytics',
    company: 'RevTech',
    desc: 'Enterprise AR analytics: multi-location roll-up, payer performance benchmarking, production-to-collection waterfall, real-time claim status, and Power BI integration.',
    color: '#1565c0',
  },
  {
    name: 'ClaimVision',
    category: 'Claim Intelligence',
    company: 'RevTech',
    desc: 'Pre-submission claim scrubbing, eligibility verification, and coding accuracy checks. Surfaces high-risk claims before adjudication — reducing denials at the front end.',
    color: '#6a1b9a',
  },
  {
    name: 'PayerIQ',
    category: 'Payer Analytics',
    company: 'RevTech',
    desc: 'Payer contract analytics: compare contracted rates to actual reimbursements, identify underpayments, benchmark payer performance, and support contract renegotiation.',
    color: '#004d40',
  },
  {
    name: 'DataVault',
    category: 'Data Integration & Warehouse',
    company: 'NDS',
    desc: 'Dental data warehouse with native connectors for Dentrix, Eaglesoft, Open Dental, CareStack, and Planet DDS. ETL pipeline + normalization + BI delivery for multi-PMS DSOs.',
    color: '#37474f',
  },
  {
    name: 'Exocomps',
    category: 'IT Fleet Management',
    company: 'NDS',
    desc: 'Remote monitoring and management for dental office IT: fleet visibility, patch management, remote scripts, alerting, hardware inventory, and compliance reporting across all locations.',
    color: '#1a237e',
  },
  {
    name: 'Xentrios PMS',
    category: 'Practice Management',
    company: 'Xentrios',
    desc: 'Cloud-native dental PMS purpose-built for DSOs: scheduling, clinical records, insurance billing, patient engagement, multi-location management, and native RevTech integration.',
    color: 'var(--xen-navy)',
  },
];

const comparisons = [
  { feature: 'Shared claim data model across all tools', stack: true, point: false },
  { feature: 'Multi-PMS data integration (Dentrix, Eaglesoft, Open Dental)', stack: true, point: false },
  { feature: 'Single data warehouse for all analytics', stack: true, point: false },
  { feature: 'Denial-to-reconciliation automated handoff', stack: true, point: false },
  { feature: 'Enterprise AR roll-up without spreadsheet consolidation', stack: true, point: false },
  { feature: 'Role-based access across the full product suite', stack: true, point: false },
  { feature: 'Single vendor relationship and support contract', stack: true, point: false },
  { feature: 'PMS and RCM on the same underlying data', stack: true, point: false },
  { feature: 'IT fleet management included', stack: true, point: false },
  { feature: 'Number of integration projects to manage', stack: '0', point: '5–8' },
];

const integrations = ['Dentrix', 'Eaglesoft', 'Open Dental', 'CareStack', 'Planet DDS', 'Xentrios'];

export default function DSOPlatformPage() {
  const cmsPage = useCmsPage('xentrios', 'dso-platform');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'RCM, Analytics, and Data Warehouse Platform for Dental Groups');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'RevTech Partners, National Data Solutions, and Xentrios provide an integrated dental operating intelligence stack for DSOs: RCM automation, denial management, reconciliation, analytics, data warehousing, PMS integration, and cloud-native practice management.');
  const painPointItems = c(cmsPage, 'features', 'items', painPoints);
  const productItems = c(cmsPage, 'benefits', 'items', products);
  const comparisonItems = c(cmsPage, 'workflow', 'items', comparisons);
  const integrationItems = c(cmsPage, 'integrations', 'items', integrations);
  return (
    <div>
      <Helmet>
        <title>RCM, Analytics, and Data Warehouse Platform for Dental Groups</title>
        <meta
          name="description"
          content="The RevTech + NDS + Xentrios platform delivers RCM automation, dental analytics, data warehousing, denial management, reconciliation, and cloud PMS in a single integrated stack for U.S. dental groups and DSOs."
        />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="xen-hero">
        <div className="xen-container">
          <p className="xen-hero-eyebrow">DSO Operating Platform</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
          <div className="xen-hero-actions">
            <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '1rem' }}>
              Request a Demo
            </Link>
            <Link
              to="/stack"
              className="xen-btn xen-btn-outline"
              style={{ padding: '0.75rem 2rem', fontSize: '1rem', borderColor: '#fff', color: '#fff' }}
            >
              See the Full Stack
            </Link>
          </div>
        </div>
      </section>

      {/* What DSOs actually need — three-column pain points */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Problems We Solve</p>
          <h2>What DSOs actually need</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            A dental support organization faces three distinct operational categories — each with its own
            set of pain points, and each requiring a different class of technology.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.75rem',
            }}
          >
            {painPointItems.map((pp) => (
              <div
                key={pp.category}
                style={{
                  background: '#fff',
                  borderRadius: '14px',
                  padding: '2rem',
                  boxShadow: '0 2px 14px rgba(0,0,0,0.08)',
                  borderTop: `5px solid ${pp.color}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.25rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.75rem' }}>{pp.icon}</span>
                  <h3 style={{ color: pp.color, margin: 0, fontSize: '1.05rem' }}>{pp.category}</h3>
                </div>
                <ul style={{ paddingLeft: '1.15rem', margin: 0, lineHeight: 1.75, color: '#555', fontSize: '0.875rem' }}>
                  {pp.problems.map((prob) => (
                    <li key={prob}>{prob}</li>
                  ))}
                </ul>
                <div
                  style={{
                    background: '#f4f6fb',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontSize: '0.85rem',
                    color: '#333',
                    lineHeight: 1.6,
                    borderLeft: `3px solid ${pp.color}`,
                  }}
                >
                  <strong style={{ display: 'block', marginBottom: '0.35rem', color: pp.color }}>Solution</strong>
                  {pp.solution}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full product catalog */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">Full Product Catalog</p>
          <h2>Every product in the stack.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Eight products covering the full operational surface of a U.S. dental group —
            all sharing the same underlying data model.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '1.25rem',
            }}
          >
            {productItems.map((p) => (
              <div
                key={p.name}
                style={{
                  background: '#f8f9ff',
                  borderRadius: '12px',
                  padding: '1.75rem',
                  textAlign: 'left',
                  borderLeft: `4px solid ${p.color}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{ color: p.color, margin: 0, fontSize: '1.1rem' }}>{p.name}</h3>
                  <span
                    style={{
                      background: p.color,
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.6rem',
                      borderRadius: '20px',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {p.company}
                  </span>
                </div>
                <div style={{ fontSize: '0.75rem', color: '#888', fontWeight: 600, marginBottom: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {p.category}
                </div>
                <p style={{ color: '#555', fontSize: '0.875rem', lineHeight: 1.65, margin: 0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Built for U.S. DSOs */}
      <section className="xen-section xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">Who It's For</p>
          <h2>Built for U.S. dental support organizations</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            The platform is purpose-built for the organizational and operational reality of a growing U.S. dental group.
          </p>
          <div className="xen-two-col" style={{ gap: '3rem' }}>
            <div>
              <p>
                A dental support organization isn't a single dental practice that has scaled up — it's a
                fundamentally different kind of organization. It operates across multiple states with different
                fee schedules and payer contracts. It has acquired practices running different PMS systems
                at different points in their technology lifecycle. It has a centralized RCM team supporting
                dozens or hundreds of locations, and a leadership layer that needs enterprise-level financial
                visibility rather than location-by-location reports.
              </p>
              <p style={{ marginTop: '1rem' }}>
                The RevTech + NDS + Xentrios stack is architected for that reality. Multi-location data
                aggregation is a core architectural assumption, not an afterthought. Role-based access
                controls allow location managers to see their data while RCM directors see the enterprise
                view. Payer logic covers every major U.S. dental insurance carrier. And the data model
                is designed around the multi-entity, multi-PMS, multi-state operating environment that
                defines how dental groups actually work.
              </p>
            </div>
            <div>
              <div
                style={{
                  background: '#fff',
                  borderRadius: '12px',
                  padding: '1.75rem',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.07)',
                }}
              >
                <h3 style={{ color: 'var(--xen-navy)', marginBottom: '1rem', fontSize: '1rem' }}>The platform is designed for DSOs operating:</h3>
                <ul style={{ paddingLeft: '1.15rem', lineHeight: 2, color: '#444', margin: 0 }}>
                  <li><strong>5 to 500+ locations</strong> across one or more states</li>
                  <li><strong>Multiple PMS platforms</strong> — Dentrix, Eaglesoft, Open Dental, CareStack, Planet DDS, or any combination</li>
                  <li><strong>Centralized or hybrid RCM</strong> — in-house teams, MaximizeRCM managed services, or both</li>
                  <li><strong>Multi-state payer contracts</strong> with Delta Dental, Cigna, Aetna, MetLife, Guardian, United Concordia, and regional carriers</li>
                  <li><strong>Active acquisition programs</strong> that add new locations and new PMS environments on a rolling basis</li>
                  <li><strong>Analytics requirements</strong> from CFOs, PE sponsors, or board-level reporting stakeholders</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stack vs. point solutions comparison table */}
      <section className="xen-section xen-section-center" style={{ background: '#fff' }}>
        <div className="xen-container">
          <p className="xen-section-eyebrow">The Comparison</p>
          <h2>How the stack compares to buying point solutions</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Most DSOs arrive at this platform after experiencing the cost and complexity of managing
            five or more separate vendor relationships that don't share data.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.9rem',
                textAlign: 'left',
                background: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 2px 16px rgba(0,0,0,0.08)',
              }}
            >
              <thead>
                <tr style={{ background: 'var(--xen-navy)', color: '#fff' }}>
                  <th style={{ padding: '1rem 1.25rem', fontWeight: 700, width: '50%' }}>Feature</th>
                  <th style={{ padding: '1rem 1.25rem', fontWeight: 700, width: '25%', textAlign: 'center' }}>
                    This Stack
                  </th>
                  <th style={{ padding: '1rem 1.25rem', fontWeight: 700, width: '25%', textAlign: 'center' }}>
                    Multiple Vendors
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonItems.map((row, i) => (
                  <tr
                    key={row.feature}
                    style={{
                      background: i % 2 === 0 ? '#f8f9ff' : '#fff',
                      borderBottom: '1px solid #e8eaf6',
                    }}
                  >
                    <td style={{ padding: '0.875rem 1.25rem', color: '#333' }}>{row.feature}</td>
                    <td style={{ padding: '0.875rem 1.25rem', textAlign: 'center' }}>
                      {typeof row.stack === 'boolean' ? (
                        <span
                          style={{
                            display: 'inline-block',
                            background: row.stack ? '#e8f5e9' : '#ffebee',
                            color: row.stack ? '#2e7d32' : '#c62828',
                            borderRadius: '20px',
                            padding: '0.2rem 0.9rem',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                          }}
                        >
                          {row.stack ? '✓ Yes' : '✗ No'}
                        </span>
                      ) : (
                        <strong style={{ color: '#2e7d32' }}>{row.stack}</strong>
                      )}
                    </td>
                    <td style={{ padding: '0.875rem 1.25rem', textAlign: 'center' }}>
                      {typeof row.point === 'boolean' ? (
                        <span
                          style={{
                            display: 'inline-block',
                            background: row.point ? '#e8f5e9' : '#ffebee',
                            color: row.point ? '#2e7d32' : '#c62828',
                            borderRadius: '20px',
                            padding: '0.2rem 0.9rem',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                          }}
                        >
                          {row.point ? '✓ Yes' : '✗ No'}
                        </span>
                      ) : (
                        <strong style={{ color: '#c62828' }}>{row.point}</strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: '1.5rem', color: '#777', fontSize: '0.875rem', textAlign: 'left', maxWidth: '720px' }}>
            Point solutions aren't inherently wrong — but when they don't share data, every integration
            is an IT project. Every handoff between systems is a manual step. Every vendor contract is
            a separate renewal and support relationship. For a DSO managing 50+ locations, this overhead
            compounds quickly.
          </p>
        </div>
      </section>

      {/* PMS integration badges */}
      <section className="xen-section xen-section-center xen-section-light">
        <div className="xen-container">
          <p className="xen-section-eyebrow">PMS Integrations</p>
          <h2>Works with your existing PMS — or replace it with Xentrios.</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2rem' }}>
            The NDS DataVault layer integrates with every major dental PMS in the United States.
            For DSOs moving to a modern cloud PMS, Xentrios is purpose-built for the platform.
          </p>
          <div className="xen-integrations">
            {integrationItems.map((name) => (
              <div key={name} className="xen-integration-badge">
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="xen-section xen-section-dark xen-section-center">
        <div className="xen-container">
          <h2>See the full DSO platform in action.</h2>
          <p className="xen-section-subtitle" style={{ marginTop: '1rem', marginBottom: '2rem' }}>
            Tell us your current PMS environment, location count, and biggest operational pain points.
            We'll show you exactly how the RevTech + NDS + Xentrios stack maps to your organization —
            and what the revenue impact of the integrated approach looks like for a group your size.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.05rem' }}>
            Request a Demo
          </Link>
        </div>
      </section>
    </div>
  );
}
